import { geocoderUnavailable } from './nominatim';
import type { Dictionary } from '../i18n';
import {
  ACCEPTED_IMAGE_MIME_TYPES,
  MAX_PHOTO_SIZE_BYTES,
  MAX_PHOTO_SIZE_MB,
  MAX_PHOTOS,
  MAX_PLAN_SIZE_BYTES,
  MAX_PLAN_SIZE_MB,
  RECOMMENDED_MIN_PHOTOS,
  type ApartmentInfo,
  type OwnerContact,
  type SubmissionFiles,
} from '../domain/types';

/**
 * Client-side validation.
 *
 * This exists to give fast, kind feedback — never as a security boundary. The
 * server re-validates everything independently and is the only check that counts.
 *
 * Errors are keyed by field name so the form can render them inline and move
 * focus to the first offender.
 */

export type Errors = Record<string, string>;

/** Field order drives which error gets focus, so it must match the visual order. */
export const STEP1_FIELD_ORDER = ['address', 'suprafataUtila'] as const;
export const STEP2_FIELD_ORDER = ['stareActuala', 'photos', 'plan', 'situatieJuridica'] as const;
export const STEP3_FIELD_ORDER = ['name', 'email', 'phone', 'consent'] as const;

/**
 * Parse a number the way a Romanian actually writes one.
 *
 * Romanian notation is the inverse of English: "." groups thousands and ","
 * is the decimal separator. A naive `parseFloat` reads "1.500" as 1.5. The
 * grouping cases below are load-bearing, not politeness.
 */
export function parseDecimal(raw: string): number | null {
  let s = raw.trim();
  if (s === '') return null;

  s = s.replace(/[\s ]/g, '');

  const lastComma = s.lastIndexOf(',');
  const lastDot = s.lastIndexOf('.');

  if (lastComma !== -1 && lastDot !== -1) {
    if (lastComma > lastDot) s = s.replace(/\./g, '').replace(',', '.');
    else s = s.replace(/,/g, '');
  } else if (lastComma !== -1) {
    s = s.replace(',', '.');
  } else if (lastDot !== -1 && /^\d{1,3}(\.\d{3})+$/.test(s)) {
    s = s.replace(/\./g, '');
  }

  const value = Number.parseFloat(s);
  return Number.isFinite(value) ? value : null;
}

/** A useful surface bigger than this is almost certainly a typo. */
const MAX_SUPRAFATA_SQM = 1_000_000;

export function isAcceptedImageFile(file: File): boolean {
  // A browser can report an empty or wrong type (HEIC often comes through as
  // ''); the extension is the fallback hint. The server sniffs magic bytes and
  // is the real gate.
  if ((ACCEPTED_IMAGE_MIME_TYPES as readonly string[]).includes(file.type)) return true;
  return /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name);
}

export function validatePhoto(file: File, t: Dictionary): string | null {
  if (!isAcceptedImageFile(file)) return t.validation.photoType;
  if (file.size > MAX_PHOTO_SIZE_BYTES) return t.validation.photoTooLarge(MAX_PHOTO_SIZE_MB);
  return null;
}

export function validatePlan(file: File, t: Dictionary): string | null {
  if (!isAcceptedImageFile(file)) return t.validation.photoType;
  if (file.size > MAX_PLAN_SIZE_BYTES) return t.validation.photoTooLarge(MAX_PLAN_SIZE_MB);
  return null;
}

/**
 * Step 1 gate: an address and a surface. Everything else on the step is optional
 * and validated only for shape (a surface, once typed, must be a real number).
 */
export function validateStep1(apartment: ApartmentInfo, t: Dictionary): Errors {
  const errors: Errors = {};

  if (apartment.address.trim() === '') errors['address'] = t.validation.addressRequired;
  // Adresa trebuie ALEASĂ din sugestii (lat/lng setate la pick, șterse la tastare
  // liberă) — altfel zona nu se poate rezolva pe harta de cartiere și evaluarea cade
  // pe banda generică. Excepția unică: geocoderul e picat — nu blocăm lead-ul pe o
  // pană externă; evaluarea rămâne posibilă, doar cu interval mai larg.
  else if (apartment.lat === null && !geocoderUnavailable())
    errors['address'] = t.validation.addressPickFromList;

  const suprafata = parseDecimal(apartment.suprafataUtila);
  if (suprafata === null || suprafata <= 0) errors['suprafataUtila'] = t.validation.suprafataInvalid;
  else if (suprafata > MAX_SUPRAFATA_SQM) errors['suprafataUtila'] = t.validation.suprafataTooLarge;

  return errors;
}

/**
 * Step 2 is entirely optional.
 *
 * Nothing here is required — photos included. Photos are recommended, not
 * mandatory, exactly like the certificate on the land tool: an owner may pass
 * through without attaching any, and a non-blocking notice on step 3 (see
 * `photosMissingSeverity`) nudges without ever refusing the submission. The only
 * things that can fail are files the owner actually attached wrongly (a .exe
 * renamed to .jpg, an oversize file, more than the maximum count).
 */
export function validateStep2(files: SubmissionFiles, t: Dictionary): Errors {
  const errors: Errors = {};

  if (files.photos.length > MAX_PHOTOS) {
    errors['photos'] = t.validation.tooManyPhotos(MAX_PHOTOS);
  } else {
    for (const entry of files.photos) {
      const photoError = validatePhoto(entry.file, t);
      if (photoError) {
        errors['photos'] = photoError;
        break;
      }
    }
  }

  if (files.plan) {
    const planError = validatePlan(files.plan.file, t);
    if (planError) errors['plan'] = planError;
  }

  return errors;
}

/**
 * Drives the non-blocking notice on step 3. Never blocks submission.
 * 'none' when no photo was attached, 'few' below the recommended count, null
 * once enough are present.
 */
export function photosMissingSeverity(files: SubmissionFiles): 'none' | 'few' | null {
  if (files.photos.length === 0) return 'none';
  if (files.photos.length < RECOMMENDED_MIN_PHOTOS) return 'few';
  return null;
}

/**
 * Deliberately permissive. An over-strict pattern rejects real addresses, and
 * the only way to truly validate an address is to send mail to it. The server
 * applies the same shape.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Romanian numbers may be written 0722 123 456, +40 722 123 456, 0040722123456.
 * Strip formatting and require a plausible digit count rather than a strict shape.
 */
export function isPlausiblePhone(raw: string): boolean {
  const digits = raw.replace(/[^\d]/g, '');
  return digits.length >= 9 && digits.length <= 15;
}

export function validateStep3(contact: OwnerContact, t: Dictionary): Errors {
  const errors: Errors = {};

  if (contact.name.trim() === '') errors['name'] = t.validation.nameRequired;

  if (contact.email.trim() === '') errors['email'] = t.validation.emailRequired;
  else if (!EMAIL_PATTERN.test(contact.email.trim())) errors['email'] = t.validation.emailInvalid;

  if (contact.phone.trim() === '') errors['phone'] = t.validation.phoneRequired;
  else if (!isPlausiblePhone(contact.phone)) errors['phone'] = t.validation.phoneInvalid;

  if (!contact.consent) errors['consent'] = t.validation.consentRequired;

  return errors;
}

/** Move focus to the first field in `order` that has an error. */
export function focusFirstError(errors: Errors, order: readonly string[]): void {
  const first = order.find((field) => errors[field]);
  if (!first) return;
  const el = document.querySelector<HTMLElement>(`[data-field="${first}"]`);
  if (!el) return;
  el.focus({ preventScroll: true });
  el.scrollIntoView({ block: 'center', behavior: 'smooth' });
}
